import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EyeIcon, EyeOffIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { api } from '@/lib/api'

const setPasswordFormSchema = z
  .object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type SetPasswordFormValues = z.infer<typeof setPasswordFormSchema>

interface InvitationInfo {
  email: string
  organization: {
    name: string
  }
}

interface ValidateInvitationResponse {
  data?: {
    email: string
    organization: {
      name: string
    }
  }
  error?: string
}

interface SetupPasswordResponse {
  success: boolean
  error?: string
}

export function SetupPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null)
  const [validatingToken, setValidatingToken] = useState(true)

  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const logo = useMemo(() => '/logo.png', [])

  const form = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordFormSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  useEffect(() => {
    if (!token) {
      setError('Invalid setup link. Please use the link from your invitation email.')
      setValidatingToken(false)
      return
    }

    validateSetupToken(token)
  }, [token])

  const validateSetupToken = async (setupToken: string) => {
    try {
      const result = await api.post<ValidateInvitationResponse>('/auth/validate-invitation', { token: setupToken })
      if (!result?.data) {
        throw new Error(result?.error || 'Invalid or expired invitation token')
      }

      setInvitationInfo(result.data)
    } catch (err) {
      console.error('Failed to validate setup token', err)
      setError(err instanceof Error ? err.message : 'Failed to validate setup token')
    } finally {
      setValidatingToken(false)
    }
  }

  const onSubmit = async (values: SetPasswordFormValues) => {
    if (!token) {
      setError('Setup token is missing')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await api.post<SetupPasswordResponse>('/auth/setup-password', {
        token,
        newPassword: values.newPassword,
      })

      if (result.success) {
        setSuccess(true)
        localStorage.removeItem('auth_token')

        setTimeout(() => {
          window.location.href = '/dashboard'
        }, 2000)
      } else {
        setError(result.error || 'Failed to set password')
      }
    } catch (err) {
      console.error('Password setup failed', err)
      setError('Failed to set password')
    } finally {
      setIsLoading(false)
    }
  }

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-clean p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground mx-auto mb-4"></div>
            <p className="text-muted-foreground">Validating invitation...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error && !invitationInfo) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-clean p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-destructive text-2xl">⚠</span>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Invalid Invitation</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/login">
              <Button>Go to Login</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-clean p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src={logo} alt="Gathio Logo" className="w-8 h-8" />
            <span className="text-xl font-bold text-foreground">gathio</span>
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-3 tracking-tight">
            Set Your Password
          </h2>
          {invitationInfo && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl mb-4">
              <p className="text-sm text-green-800">
                Welcome to <strong>{invitationInfo.organization.name}</strong>! Please set a secure password to complete your account setup.
              </p>
            </div>
          )}
        </div>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <p className="text-sm text-green-800">
              Password set successfully! Redirecting to your dashboard...
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground mb-2 block">
                    New Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="••••••••"
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        disabled={isLoading}
                        className="h-14 px-4 pr-12 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showNewPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs mt-1 text-destructive" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-foreground mb-2 block">
                    Confirm Password
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="••••••••"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        disabled={isLoading}
                        className="h-14 px-4 pr-12 border border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-foreground placeholder:text-muted-foreground rounded-xl bg-input transition-smooth hover:border-primary/50 shadow-clean"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs mt-1 text-destructive" />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-xl disabled:opacity-50"
              disabled={isLoading || success}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Setting password...
                </div>
              ) : success ? (
                'Password Set!'
              ) : (
                'Set Password & Join Organization'
              )}
            </Button>
          </form>
        </Form>

        {invitationInfo && (
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              Setting up account for <strong>{invitationInfo.email}</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default SetupPasswordPage
